<template>
    <div class="dashboard flex">
        <legacy-app-prompt
            :user="user"
        />
        <template v-if="!UIState.isSuspended">
            <base-notification
                v-if="userSubscriptionDetails.endsAt"
                :class="{ 'is-red': userSubscriptionDetails.endsAt }"
                @hide-user-notification="hideNotification"
            >
                <span>Your account will be suspended on {{ subscriptionEndDate }}</span>
                <router-link to="/settings/billing">
                    Click here to resume your subscription<i class="far fa-long-arrow-right" />
                </router-link>
            </base-notification>
            <mobile-nav
                :open="mobileNavOpen"
            />
            <sidebar
                class="notificationActive"
                :has-notification="showingUserNotification"
                @open-mobile-nav="handleMobileNav"
            />
            <transition
                name="fade"
                mode="out-in"
            >
                <router-view />
            </transition>
        </template>
        <template v-else>
            <sidebar
                :class="{ notificationActive: showingUserNotification }"
                :has-notification="showingUserNotification"
                @open-mobile-nav="handleMobileNav"
            />
            <finish-screen
                name="FinishCancelation"
                class="user-suspended"
            >
                <template v-slot:image>
                    <svg-vue class="cancelation-image" icon="finish-cancelation" />
                </template>
                <template v-slot:content>
                    <h2>Thank you for using Fusebox!</h2>
                    <p>Your subscription has been successfully canceled. We hoped you enjoyed using our products and hope to see you back soon!</p>
                    <p>If you change your mind, click Subscribe button below, and choose a new plan.</p>
                    <base-submit-button
                        class="btn btn-primary"
                        @click="suspendedSubscription"
                    >
                        Subscribe
                    </base-submit-button>
                </template>
            </finish-screen>
        </template>
        <transition-group
            name="fade"
            mode="out-in"
        >
            <upgrade-modal v-if="UIState.isUpgradeModalVisible" key="upgradeModal" :upgrade-user="! UIState.isSuspended" />
            <onboarding v-if="UIState.isOnboardingModalVisible" key="onboardingModal" />
            <upgrade-pro-modal v-if="UIState.isUpgradeProModalVisible" key="upgradeProModal" />
        </transition-group>
    </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import overflowBlocker from '@/utils/overflowBlocker';
import { loadScript, removeScript } from '@/utils/scriptLoader';
import moment from 'moment';

import Sidebar from '@/components/Dashboard/TheSidebar.vue';
import MobileNav from '@/components/Dashboard/TheMobileNavigation.vue';
import Onboarding from '@/components/Dashboard/Onboarding.vue';
import UpgradeModal from '@/components/Dashboard/UpgradeModal.vue';
import UpgradeProModal from '@/components/Dashboard/Settings/Billing/UpgradeProModal.vue';
import FinishScreen from '@/components/Dashboard/Offboarding/FinishScreen.vue';
import LegacyAppPrompt from '@/components/LegacyAppPrompt.vue';

export default {
    components: {
        Sidebar,
        MobileNav,
        Onboarding,
        UpgradeModal,
        UpgradeProModal,
        FinishScreen,
        LegacyAppPrompt,
    },

    data: () => ({
        hideUserNotification: false,
        mobileNavOpen: false,
        helpScountbeacon: {
            freeId: 'e0e1c51c-1192-47e3-b80d-c5bf6abee04d',
            proId: '7e4e98f9-1639-4f79-b25a-206e3af7f0d8',
        },
    }),

    computed: {
        ...mapState(['user', 'UIState', 'userSubscription']),
        ...mapGetters(['userSubscriptionDetails']),

        /**
         * Returns user subscription end date
         *
         * @returns {string} Formated date
         */
        subscriptionEndDate() {
            return this.userSubscriptionDetails.endsAt ? moment.unix(this.userSubscriptionDetails.endsAt).format('MMMM D, YYYY') : '';
        },

        /**
         * Returns flag for user notification
         *
         * @returns {boolean} Wether to show user notification.
         */
        showingUserNotification: {
            /**
             * Getter
             *
             * @returns {boolean} Wether to show user notification.
             */
            get() {
                return true === this.hideUserNotification
                    ? false
                    : !! this.user.suspended_at && !! this.userSubscriptionDetails.endsAt;
            },

            /**
             * Setter
             *
             * @param  {boolean} value Whether to show user notification.
             */
            set(value) {
                this.hideUserNotification = ! value;
            },
        },
    },

    watch: {
        mobileNavOpen: {
            immediate: true,

            /**
             * Handles mobile menu value
             *
             * @param   {boolean}  newVal  New value from mobile btn.
             *
             */
            handler(newVal) {
                overflowBlocker(newVal);
            },
        },

        /**
         * Watch user change
         */
        user: {
            immediate: true,

            /**
             * Handle user status change
             *
             *  @param   {object}  newVal  New value from vuex store.
             *  @param   {object}  oldVal  Old value from vuex store.
             */
            handler(newVal, oldVal) {
                if (oldVal && oldVal.pro) {
                    return;
                }

                if (newVal.pro && window.Beacon) {
                    window.Beacon('destroy');
                    this.loadHelpScout();
                }
            },
        },
    },

    /**
     * Mounted hook
     *
     */
    mounted() {
        const {
            hide_onboarding,
            onboarding_finished,
        } = this.user.meta_data;

        if (
            ! this.user.suspended_at &&
            ! onboarding_finished &&
            ! hide_onboarding &&
            '#upgrade' !== this.$route.hash
        ) {
            this.changeUIState({ isOnboardingModalVisible: true });
        }

        loadScript('fast.wistia.com/assets/external/E-v1.js', 'wistia_embed', null);

        const app = document.getElementById('app');

        app.classList.add('full-height');

        this.loadHelpScout();

        this.$once('hook:beforeDestroy', () => {
            app.classList.remove('full-height');
            app.classList.remove('full-height');
            removeScript('wistia_embed');
        });
    },

    /**
     * Before destroy hook
     *
     */
    beforeDestroy() {
        window.Beacon('destroy');
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Handles mobile nav
         *
         * @param   {boolean}  value  True/false for mobile nav.
         *
         */
        handleMobileNav(value) {
            this.mobileNavOpen = value;
        },

        /**
         * Hide user notification
         *
         */
        hideNotification() {
            this.showingUserNotification = false;
        },

        /**
         * Load Help Scout Beacon script
         *
         */
        loadHelpScout() {
            /* eslint-disable */
            !function(e,t,n){
                function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){}
                );
            /* eslint-enable */

            if (this.user.pro) {
                window.Beacon('init', this.helpScountbeacon.proId);
            } else {
                window.Beacon('init', this.helpScountbeacon.freeId);
            }
        },

        /**
         * Open upgrade modal for suspended user
         */
        suspendedSubscription() {
            this.changeUIState({ isUpgradeModalVisible: true });
        },
    },
};
</script>
